import React, { useState, useEffect } from 'react';
import { Button, Spinner, Tab, Tabs } from 'react-bootstrap';
import styles from './ImagePlugin.module.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand
} from 'lexical';
import { $createImageNode, $isImageNode, ImageNode } from './ImageNodes';

const INSERT_IMAGE_COMMAND = createCommand('INSERT_IMAGE_COMMAND');
const initialState = {
  src: '',
  altText: '',
  width: '',
  height: ''
};

const ImagePlugin = () => {
  const [selectedTab, setSelectedTab] = useState('upload');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showTabs, setShowTabs] = useState(false);
  const [values, setValues] = useState(initialState);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [editor] = useLexicalComposerContext();

  const onImageClick = () => {
    setShowTabs(!showTabs);
  };

  const onSelectTab = (tab) => {
    setSelectedTab(tab);
  };

  const handleChange = (e) => {
    const inputUrl = e.target.value;
    setValues({
      ...values,
      src: inputUrl
    });
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    setIsValidUrl(urlRegex.test(inputUrl));
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const dispatchImageUpload = (payload) => {
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    setValues(initialState);
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const onImageUpload = async () => {
    if (selectedFiles !== null || selectedFiles.length > 0) {
      const base64 = await getBase64(selectedFiles[0]);
      const payload = {
        ...values,
        src: base64,
        altText: 'image'
      };
      dispatchImageUpload(payload);
      setSelectedFiles([]);
      setShowTabs(false);
    }
  };

  const onLinkUpload = () => {
    const payload = {
      ...values,
      altText: 'image'
    };
    values.src && dispatchImageUpload(payload);
    setShowTabs(false);
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    });
  };

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode({
            ...payload
          });
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      )
    );
  }, [editor]);

  return (
    <div className={styles.container}>
      <div className={styles.format} onClick={onImageClick}>
        <img src="assets/icons/editor-toolbar/image-file.svg" />
      </div>
      {showTabs && (
        <div className={styles.tabContainer}>
          <Tabs onSelect={onSelectTab} activeKey={selectedTab} className={styles.tabs}>
            <Tab eventKey="upload" title="Upload" className={styles.tab}>
              <div className={styles.tabContent}>
                <div className={styles.linkInputContainer}>
                  <input type="file" multiple onChange={handleFileChange} accept="image/*" />
                </div>
                {/* <DimensionBox width={values.width} height={values.height} onDimensionChange={handleDimensionChange} /> */}
                <div className={styles.button}>
                  <Button variant="primary" disabled={selectedFiles.length === 0} size="sm" onClick={onImageUpload}>
                    Upload
                  </Button>
                </div>
              </div>
            </Tab>
            <Tab eventKey="link" title="Add URL" className={styles.tab}>
              <div className={styles.tabContent}>
                <div className={styles.linkInputContainer}>
                  <input
                    type="text"
                    placeholder="Enter Image URL"
                    value={values.src}
                    onChange={handleChange}
                    style={{ borderColor: isValidUrl ? 'initial' : 'red' }}
                  />
                  {!isValidUrl && (
                    <p style={{ color: 'red', fontSize: '12px' }}>Invalid URL. Please enter a valid URL.</p>
                  )}
                </div>
                {/* <DimensionBox width={values.width} height={values.height} onDimensionChange={handleDimensionChange} /> */}
                <div className={styles.button}>
                  <Button
                    variant="primary"
                    disabled={values.src === '' || !isValidUrl}
                    size="sm"
                    onClick={onLinkUpload}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default ImagePlugin;

const DimensionBox = ({ width, height, onDimensionChange }) => {
  return (
    <div className={styles.dimensionBox}>
      <div className={styles.dimension}>
        <span className={styles.dimensionTitle}>Width</span>
        <input className={styles.dimensionInput} type="text" value={width} onChange={onDimensionChange} name="width" />
      </div>
      <div className={styles.dimension}>
        <span className={styles.dimensionTitle}>Height</span>
        <input
          className={styles.dimensionInput}
          type="text"
          value={height}
          name="height"
          onChange={onDimensionChange}
        />
      </div>
    </div>
  );
};
